import { useI18n } from "@core/i18n/i18n";
import styled from "@emotion/styled";
import { AddPatientButton } from "@ui/pages/home/components/add-patient-button";
import PatientAddIcon from "@assets/icons/patient-add.svg";
import { stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import React, { useCallback, useContext, useMemo } from "react";
import { InviteOrCreatePatient } from "@ui/pages/home/pending-patient-dialog/components/invite-or-create-patient";
import { DialogContext, ModalId } from "@ui/components/dialog/dialog-provider";
import { DialogStep } from "@ui/pages/home/pending-patient-dialog/add-patient-dialog";
import { physioMeMobileBreakpoint } from "@ui/style";

export enum EmptyPatientMessageVariant {
	NoPatients = "NoPatients",
	NoRegisteredPatients = "NoRegisteredPatients",
	NoPendingPatients = "NoPendingPatients",
}

interface EmptyPatientMessageProps {
	variant: EmptyPatientMessageVariant;
}

export function EmptyPatientMessage({ variant }: EmptyPatientMessageProps): JSX.Element {
	const { format } = useI18n();
	const dialogContext = useContext(DialogContext);

	const onCreatePatientClicked = useCallback(
		() =>
			dialogContext?.openModal(ModalId.AddPatient, {
				contentStyle: { overflowY: "visible" },
				step: DialogStep.CreatePatientForm,
			}),
		[dialogContext],
	);

	const onJoinHctClicked = useCallback(
		() =>
			dialogContext?.openModal(ModalId.AddPatient, {
				contentStyle: { overflowY: "visible" },
				step: DialogStep.InvitationCodeForm,
			}),
		[dialogContext],
	);

	const { title, description, content } = useMemo<{
		title: string;
		description?: string;
		content: React.ReactNode;
	}>(() => {
		switch (variant) {
			case EmptyPatientMessageVariant.NoPatients:
				return {
					title: format("home.noPatients.title"),
					description: format("home.noPatients.description"),
					content: <StyledInviteOrCreatePatient createPatient={onCreatePatientClicked} joinHct={onJoinHctClicked} />,
				};
			case EmptyPatientMessageVariant.NoRegisteredPatients:
				return {
					title: format("home.noRegisteredPatients.title"),
					description: format("home.noRegisteredPatients.description"),
					content: <StyledInviteOrCreatePatient createPatient={onCreatePatientClicked} joinHct={onJoinHctClicked} />,
				};
			case EmptyPatientMessageVariant.NoPendingPatients:
				return { title: format("home.noPendingPatients.title"), content: <AddPatientButton /> };
		}
	}, [format, onCreatePatientClicked, onJoinHctClicked, variant]);

	return (
		<MainContainer>
			<Container>
				<PatientAddIcon width={128} height={128} />
				<TextContainer>
					<Title>{title}</Title>
					{description && <Description className="p-size-s">{description}</Description>}
				</TextContainer>
				{content}
			</Container>
		</MainContainer>
	);
}

const MainContainer = styled.div`
	display: grid;
	place-items: center;
`;

const Container = styled.div`
	${stack(24, "center", "center")};
	padding: 65px 16px;
	max-width: 400px;

	@media ${physioMeMobileBreakpoint} {
		padding-top: 30px;
		padding-bottom: 20px;
	}
`;

const TextContainer = styled.div`
	${stack("XS", "center", "center")};
`;

const Title = styled.h3`
	text-align: center;
	color: ${colors.grey};
`;

const Description = styled.div`
	text-align: center;
	color: ${colors.grey};
`;

const StyledInviteOrCreatePatient = styled(InviteOrCreatePatient)`
	@media ${physioMeMobileBreakpoint} {
		${stack()};
	}
`;
